import {
  RiFacebookFill,
  RiGlobalLine,
  RiInstagramFill,
  RiMailFill,
  RiMapPinFill,
  RiPhoneFill,
  RiSearchLine,
  RiShoppingCartLine,
  RiTiktokFill,
  RiTimeLine,
  RiTwitterXLine,
  RiWhatsappFill,
  RiYoutubeLine,
  RiCloseLine,
} from "@remixicon/react";
import { Button } from "@/components/ui/button";

export type SalesLinkChrome = {
  primary: string;
  secondary: string;
  accent: string;
  storeName: string;
  logo?: string;
  heroTitle: string;
  bio: string;
  businessType?: string;
  bannerEnabled: boolean;
  bannerUrl?: string;
  showHero: boolean;
  showSearch: boolean;
  formattedAddress: string;
  whatsappHref: string | null;
  whatsapp?: string;
  email?: string;
  mapsHref: string | null;
  hasSocialLinks: boolean;
  social?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
  };
  hasBusinessHours: boolean;
  businessHours?: Array<{
    day: string;
    open: boolean;
    openTime: string;
    closeTime: string;
  }>;
  searchQuery: string;
  onSearch: (value: string) => void;
  onOpenMobileSearch: () => void;
  itemCount: number;
  onOpenCart: () => void;
  hideCart: boolean;
};

function CartButton({ chrome, light = false }: { chrome: SalesLinkChrome; light?: boolean }) {
  if (chrome.hideCart) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={chrome.onOpenCart}
      className={`relative h-10 rounded-full px-3.5 ${
        light
          ? "border-white/30 bg-white/15 text-white hover:!bg-white/25 hover:!text-white"
          : "border-black/10 text-black/80 hover:!bg-black/[0.03] hover:!text-black"
      }`}
    >
      <RiShoppingCartLine className="h-4 w-4 sm:mr-2" />
      <span className="hidden sm:inline">Cart</span>
      {chrome.itemCount > 0 && (
        <span
          className="ml-1.5 rounded-full px-1.5 py-0.5 text-xs font-semibold text-white"
          style={{ backgroundColor: chrome.accent }}
        >
          {chrome.itemCount}
        </span>
      )}
    </Button>
  );
}

function SearchField({ chrome, className = "" }: { chrome: SalesLinkChrome; className?: string }) {
  if (!chrome.showSearch || chrome.hideCart) return null;
  return (
    <div className={`relative ${className}`}>
      <RiSearchLine className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-black/35" />
      <input
        type="text"
        placeholder="Search products..."
        value={chrome.searchQuery}
        onChange={(event) => chrome.onSearch(event.target.value)}
        className="h-10 w-full rounded-full border border-black/8 bg-black/[0.03] pl-10 pr-9 text-sm outline-none focus:border-black/15 focus:bg-white"
      />
      {chrome.searchQuery && (
        <button
          type="button"
          onClick={() => chrome.onSearch("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-black/35"
          aria-label="Clear search"
        >
          <RiCloseLine className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

function ContactRow({ chrome, align = "center" }: { chrome: SalesLinkChrome; align?: "center" | "start" }) {
  const justify = align === "center" ? "justify-center" : "justify-start";
  return (
    <div className={`flex flex-wrap items-center gap-2.5 ${justify}`}>
      {chrome.whatsappHref && (
        <a
          href={chrome.whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-11 items-center gap-2 rounded-full px-5 text-sm font-semibold text-white"
          style={{ backgroundColor: chrome.primary }}
        >
          <RiWhatsappFill className="h-4 w-4" />
          WhatsApp
        </a>
      )}
      {chrome.whatsapp && (
        <a href={`tel:${chrome.whatsapp}`} className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-semibold">
          <RiPhoneFill className="h-4 w-4" style={{ color: chrome.primary }} />
          Call
        </a>
      )}
      {chrome.email && (
        <a href={`mailto:${chrome.email}`} className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-semibold">
          <RiMailFill className="h-4 w-4" style={{ color: chrome.primary }} />
          Email
        </a>
      )}
      {chrome.mapsHref && (
        <a href={chrome.mapsHref} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full border border-black/10 bg-white px-5 text-sm font-semibold">
          <RiMapPinFill className="h-4 w-4" style={{ color: chrome.accent }} />
          Directions
        </a>
      )}
    </div>
  );
}

function SocialRow({ chrome }: { chrome: SalesLinkChrome }) {
  if (!chrome.hasSocialLinks) return null;
  const items = [
    chrome.social?.instagram && { href: chrome.social.instagram, label: "Instagram", icon: RiInstagramFill },
    chrome.social?.facebook && { href: chrome.social.facebook, label: "Facebook", icon: RiFacebookFill },
    chrome.social?.twitter && { href: chrome.social.twitter, label: "X", icon: RiTwitterXLine },
    chrome.social?.tiktok && { href: chrome.social.tiktok, label: "TikTok", icon: RiTiktokFill },
    chrome.social?.youtube && { href: chrome.social.youtube, label: "YouTube", icon: RiYoutubeLine },
    chrome.social?.website && { href: ensureHttp(chrome.social.website), label: "Website", icon: RiGlobalLine },
  ].filter(Boolean) as { href: string; label: string; icon: typeof RiGlobalLine }[];
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <a
          key={item.label}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={item.label}
          className="inline-flex size-10 items-center justify-center rounded-full border border-black/10 bg-white"
          style={{ color: chrome.primary }}
        >
          <item.icon className="h-4 w-4" />
        </a>
      ))}
    </div>
  );
}

function Hours({ chrome }: { chrome: SalesLinkChrome }) {
  if (!chrome.hasBusinessHours || !chrome.businessHours) return null;
  return (
    <div className="rounded-[1.5rem] border border-black/5 bg-white px-5 py-4">
      <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold">
        <RiTimeLine className="h-4 w-4" style={{ color: chrome.primary }} />
        Hours
      </h3>
      <div className="space-y-1.5">
        {chrome.businessHours.map((hours, index) => (
          <div key={`${hours.day}-${index}`} className="flex items-center justify-between text-sm">
            <span className="text-black/60">{hours.day}</span>
            <span className="text-black/45">{hours.open ? `${hours.openTime} – ${hours.closeTime}` : "Closed"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ensureHttp(url: string) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function HeroMedia({ chrome, className, gradient }: { chrome: SalesLinkChrome; className: string; gradient: string }) {
  const photo = chrome.bannerEnabled && chrome.bannerUrl;
  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={
        photo
          ? { backgroundImage: `url(${chrome.bannerUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
          : { background: gradient }
      }
      role={photo ? "img" : undefined}
      aria-label={photo ? "Store banner" : undefined}
    >
      {!photo && (
        <div className="absolute inset-0 flex items-center justify-center">
          {chrome.logo ? (
            <img src={chrome.logo} alt="" className="size-24 rounded-full object-cover ring-4 ring-white/80" />
          ) : (
            <span className="flex size-24 items-center justify-center rounded-full bg-white/20 text-4xl font-black text-white">
              {chrome.storeName.charAt(0).toUpperCase() || "S"}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Mark({ chrome, rounded = "full" }: { chrome: SalesLinkChrome; rounded?: "full" | "xl" }) {
  const shape = rounded === "full" ? "rounded-full" : "rounded-2xl";
  if (chrome.logo) {
    return <img src={chrome.logo} alt="" className={`size-11 object-cover ${shape}`} />;
  }
  return (
    <span className={`flex size-11 items-center justify-center text-base font-bold text-white ${shape}`} style={{ backgroundColor: chrome.primary }}>
      {chrome.storeName.charAt(0).toUpperCase()}
    </span>
  );
}

export function EditorialChrome({ chrome }: { chrome: SalesLinkChrome }) {
  return (
    <>
      <header className="sticky top-0 z-50 border-b border-black/5 bg-[#faf9f6]/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-3 px-4">
          <Mark chrome={chrome} />
          <p className="min-w-0 flex-1 truncate font-heading text-sm font-semibold tracking-[0.14em] uppercase">
            {chrome.storeName}
          </p>
          {chrome.showSearch && !chrome.hideCart && (
            <button type="button" className="rounded-full p-2 sm:hidden" onClick={chrome.onOpenMobileSearch} aria-label="Search">
              <RiSearchLine className="h-5 w-5" />
            </button>
          )}
          {chrome.showSearch && !chrome.hideCart && (
            <div className="hidden w-64 sm:block">
              <SearchField chrome={chrome} />
            </div>
          )}
          <CartButton chrome={chrome} />
        </div>
      </header>
      <section className="mx-auto max-w-5xl px-4 pb-8 pt-12 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.22em]" style={{ color: chrome.accent }}>
          {chrome.businessType || "Store"}
        </p>
        <h2 className="mx-auto mt-3 max-w-3xl font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-6xl" style={{ color: chrome.primary }}>
          {chrome.heroTitle}
        </h2>
        <div className="mx-auto mt-5 h-1 w-16 rounded-full" style={{ backgroundColor: chrome.accent }} />
        {chrome.bio && <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-black/60">{chrome.bio}</p>}
        {chrome.showHero && (
          <HeroMedia
            chrome={chrome}
            className="mx-auto mt-8 h-64 rounded-[2rem] sm:h-80"
            gradient={`linear-gradient(145deg, ${chrome.primary}, ${chrome.secondary})`}
          />
        )}
        <div className="mt-8">
          <ContactRow chrome={chrome} />
        </div>
        {chrome.hasSocialLinks && (
          <div className="mt-5 flex justify-center">
            <SocialRow chrome={chrome} />
          </div>
        )}
        {chrome.formattedAddress && (
          <p className="mt-4 text-sm text-black/50">{chrome.formattedAddress}</p>
        )}
        {chrome.hasBusinessHours && (
          <div className="mx-auto mt-8 max-w-md text-left">
            <Hours chrome={chrome} />
          </div>
        )}
      </section>
    </>
  );
}

export function ShowcaseChrome({ chrome }: { chrome: SalesLinkChrome }) {
  return (
    <>
      <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur-md" style={{ borderColor: `${chrome.accent}55` }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4">
          <Mark chrome={chrome} rounded="xl" />
          <div className="min-w-0">
            <p className="truncate font-heading text-base font-bold" style={{ color: chrome.primary }}>{chrome.storeName}</p>
            <p className="truncate text-[11px] font-medium uppercase tracking-[0.14em] text-black/40">{chrome.businessType || "Shop"}</p>
          </div>
          {chrome.showSearch && !chrome.hideCart && (
            <div className="mx-auto hidden w-full max-w-md flex-1 sm:block">
              <SearchField chrome={chrome} />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {chrome.showSearch && !chrome.hideCart && (
              <button type="button" className="rounded-full p-2 sm:hidden" onClick={chrome.onOpenMobileSearch} aria-label="Search">
                <RiSearchLine className="h-5 w-5" />
              </button>
            )}
            <CartButton chrome={chrome} />
          </div>
        </div>
      </header>
      <section className={`mx-auto grid max-w-6xl items-stretch gap-6 px-4 py-8 lg:py-12 ${chrome.showHero ? "lg:grid-cols-[1.1fr_0.9fr]" : ""}`}>
          <div className="flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: chrome.accent }}>
              {chrome.storeName}
            </p>
            <h2 className="mt-2 font-display text-4xl font-black leading-[0.95] tracking-tight sm:text-5xl" style={{ color: chrome.primary }}>
              {chrome.heroTitle}
            </h2>
            {chrome.bio && <p className="mt-4 max-w-xl text-base leading-relaxed text-black/60 sm:text-lg">{chrome.bio}</p>}
            {chrome.formattedAddress && (
              <p className="mt-3 flex items-start gap-1.5 text-sm text-black/50">
                <RiMapPinFill className="mt-0.5 h-4 w-4 shrink-0" style={{ color: chrome.accent }} />
                {chrome.formattedAddress}
              </p>
            )}
            <div className="mt-6">
              <ContactRow chrome={chrome} align="start" />
            </div>
            {chrome.hasSocialLinks && (
              <div className="mt-4">
                <SocialRow chrome={chrome} />
              </div>
            )}
          </div>
          {chrome.showHero && (
            <HeroMedia
              chrome={chrome}
              className="min-h-64 rounded-[2rem]"
              gradient={`linear-gradient(160deg, ${chrome.primary} 0%, ${chrome.secondary} 100%)`}
            />
          )}
        </section>
      {chrome.hasBusinessHours && (
        <div className="mx-auto max-w-6xl px-4 pb-6">
          <Hours chrome={chrome} />
        </div>
      )}
    </>
  );
}
